import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type AdminFeatureFlag,
  useAdminFeatureFlagsApi,
  useAdminTenantsApi,
  useUpdateAdminFeatureFlagApi,
} from "hooks/useAdminApi";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";

// カテゴリは分類タグなので tag-neutral で統一

/**
 * Feature Flag 管理ページ
 * @return {JSX.Element} Feature Flag 管理ページ
 */
export const FeatureFlagsPage: FC = () => {
  const { data, loading, error, refetch } = useAdminFeatureFlagsApi();
  const { data: tenantData } = useAdminTenantsApi();
  const { updateFlag, setTenantOverride, deleteTenantOverride } =
    useUpdateAdminFeatureFlagApi();

  const [actionLoading, setActionLoading] = useState(false);
  const [expandedFlag, setExpandedFlag] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [addOverrideFlag, setAddOverrideFlag] = useState<string | null>(null);

  // カテゴリ一覧を取得
  const categories = [
    ...new Set(data?.items?.map(f => f.category) || []),
  ].sort();

  // フィルタリング
  const filteredFlags =
    data?.items?.filter(
      f => categoryFilter === "all" || f.category === categoryFilter,
    ) || [];

  /**
   * グローバル enabled トグル
   * @param {AdminFeatureFlag} flag 対象フラグ
   */
  const handleToggleEnabled = async (flag: AdminFeatureFlag) => {
    setActionLoading(true);
    try {
      await updateFlag(flag.id, !flag.enabled);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * テナント override を設定
   * @param {string} flagId フラグID
   * @param {string} tenantId テナントID
   * @param {boolean} enabled 有効/無効
   */
  const handleSetOverride = async (
    flagId: string,
    tenantId: string,
    enabled: boolean,
  ) => {
    setActionLoading(true);
    try {
      await setTenantOverride(flagId, tenantId, enabled);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * テナント override を削除
   * @param {string} flagId フラグID
   * @param {string} tenantId テナントID
   */
  const handleDeleteOverride = async (flagId: string, tenantId: string) => {
    setActionLoading(true);
    try {
      await deleteTenantOverride(flagId, tenantId);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * テナント override を新規追加
   * @param {string} flagId フラグID
   * @param {string} tenantId テナントID
   */
  const handleAddOverride = async (flagId: string, tenantId: string) => {
    setAddOverrideFlag(null);
    await handleSetOverride(flagId, tenantId, true);
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>フィーチャーフラグ管理</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>フィーチャーフラグ管理</PageTitle>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin">&larr; ダッシュボードに戻る</Link>
        </Button>
      </div>

      {/* カテゴリフィルター */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setCategoryFilter("all")}
          className={`btn btn-sm ${categoryFilter === "all" ? "btn-primary" : "btn-ghost"}`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`btn btn-sm ${categoryFilter === cat ? "btn-primary" : "btn-ghost"}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* フラグ一覧テーブル */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>キー</Table.HeaderCell>
              <Table.HeaderCell>名前</Table.HeaderCell>
              <Table.HeaderCell>カテゴリ</Table.HeaderCell>
              <Table.HeaderCell>グローバル</Table.HeaderCell>
              <Table.HeaderCell>Override</Table.HeaderCell>
              <Table.HeaderCell> </Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : filteredFlags.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">
                  Feature Flag が見つかりません
                </td>
              </tr>
            ) : (
              filteredFlags.map(flag => (
                <FlagRow
                  key={flag.id}
                  flag={flag}
                  expanded={expandedFlag === flag.id}
                  actionLoading={actionLoading}
                  tenants={tenantData?.items || []}
                  addOverrideOpen={addOverrideFlag === flag.id}
                  onToggle={() => handleToggleEnabled(flag)}
                  onExpand={() =>
                    setExpandedFlag(expandedFlag === flag.id ? null : flag.id)
                  }
                  onSetOverride={(tenantId, enabled) =>
                    handleSetOverride(flag.id, tenantId, enabled)
                  }
                  onDeleteOverride={tenantId =>
                    handleDeleteOverride(flag.id, tenantId)
                  }
                  onAddOverrideOpen={() =>
                    setAddOverrideFlag(
                      addOverrideFlag === flag.id ? null : flag.id,
                    )
                  }
                  onAddOverride={tenantId =>
                    handleAddOverride(flag.id, tenantId)
                  }
                />
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* 統計 */}
      <div className="mt-4 text-sm text-gray-600">
        全 {data?.total || 0} 件中 {filteredFlags.length} 件を表示
      </div>
    </div>
  );
};

// =====================================
// FlagRow component
// =====================================

type FlagRowProps = {
  flag: AdminFeatureFlag;
  expanded: boolean;
  actionLoading: boolean;
  tenants: { id: string; name: string }[];
  addOverrideOpen: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onSetOverride: (tenantId: string, enabled: boolean) => void;
  onDeleteOverride: (tenantId: string) => void;
  onAddOverrideOpen: () => void;
  onAddOverride: (tenantId: string) => void;
};

/**
 * フラグ行コンポーネント
 * @param {FlagRowProps} props プロパティ
 * @return {JSX.Element} フラグ行
 */
const FlagRow: FC<FlagRowProps> = ({
  flag,
  expanded,
  actionLoading,
  tenants,
  addOverrideOpen,
  onToggle,
  onExpand,
  onSetOverride,
  onDeleteOverride,
  onAddOverrideOpen,
  onAddOverride,
}) => {
  const overrideTenantIds = new Set(flag.overrides.map(o => o.tenantId));
  const availableTenants = tenants.filter(t => !overrideTenantIds.has(t.id));

  return (
    <>
      <tr className="hover">
        <td>
          <div>
            <code className="text-sm font-mono">{flag.key}</code>
            {flag.description && (
              <p className="text-xs text-gray-400 mt-1">{flag.description}</p>
            )}
          </div>
        </td>
        <td className="font-medium">{flag.name}</td>
        <td>
          <StatusBadge tone="tag-neutral">{flag.category}</StatusBadge>
        </td>
        <td>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={flag.enabled}
              disabled={actionLoading}
              onChange={onToggle}
            />
            <StatusBadge tone={flag.enabled ? "soft-success" : "soft-neutral"}>
              {flag.enabled ? "ON" : "OFF"}
            </StatusBadge>
          </div>
        </td>
        <td>
          {flag.overrides.length > 0 && (
            <StatusBadge tone="soft-neutral">
              {flag.overrides.length}件
            </StatusBadge>
          )}
        </td>
        <td>
          <Button onClick={onExpand} variant="ghost" size="xs" type="button">
            {expanded ? "閉じる" : "詳細"}
          </Button>
        </td>
      </tr>

      {/* 展開パネル: テナント override */}
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-base-200/50 px-8 py-4">
            <div className="text-sm font-semibold mb-3">テナント Override</div>

            {flag.overrides.length === 0 ? (
              <p className="text-sm text-gray-500 mb-3">
                テナント個別設定はありません
              </p>
            ) : (
              <div className="space-y-2 mb-3">
                {flag.overrides.map(override => (
                  <div
                    key={override.id}
                    className="flex items-center justify-between bg-base-100 rounded-lg px-4 py-2"
                  >
                    <span className="text-sm">{override.tenantName}</span>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm"
                        checked={override.enabled}
                        disabled={actionLoading}
                        onChange={() =>
                          onSetOverride(override.tenantId, !override.enabled)
                        }
                      />
                      <StatusBadge
                        tone={
                          override.enabled ? "soft-success" : "soft-neutral"
                        }
                      >
                        {override.enabled ? "ON" : "OFF"}
                      </StatusBadge>
                      <Button
                        type="button"
                        variant="destructive"
                        size="xs"
                        onClick={() => onDeleteOverride(override.tenantId)}
                        disabled={actionLoading}
                      >
                        削除
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* テナント追加 */}
            {availableTenants.length > 0 && (
              <div className="relative inline-block">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={onAddOverrideOpen}
                >
                  + テナント追加
                </Button>
                {addOverrideOpen && (
                  <div className="absolute z-10 mt-1 bg-base-100 border rounded-lg shadow-lg py-1 min-w-[200px]">
                    {availableTenants.map(tenant => (
                      <button
                        key={tenant.id}
                        onClick={() => onAddOverride(tenant.id)}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-base-200"
                      >
                        {tenant.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export default FeatureFlagsPage;
